import { Dispatch, SetStateAction, useCallback, useEffect, useRef, useState } from 'react';
import { getRequest, postRequest } from '@services/api/api';
import { JobResult, MiLCALogRecord } from '@typeList/api/system';

const POLLING_INTERVAL_MS = 5000;
type RequestParams = { caseStudyId: number; modelIds: number[] };

const waitForNextPoll = (signal: AbortSignal) => new Promise<void>((resolve, reject) => {
    const onAbort = () => {
        window.clearTimeout(timer);
        reject(new DOMException('上流結果の監視を終了しました', 'AbortError'));
    };
    const timer = window.setTimeout(() => {
        signal.removeEventListener('abort', onAbort);
        resolve();
    }, POLLING_INTERVAL_MS);
    signal.addEventListener('abort', onAbort, { once: true });
});

export const useUpperResultJob = <T,>(resultType: 'lci' | 'lcia') => {
    const [data, updateData] = useState<T | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [logs, setLogs] = useState<MiLCALogRecord[]>([]);
    const active = useRef<{ key: string; controller: AbortController; promise: Promise<T | null> } | null>(null);
    const lastRequest = useRef<RequestParams | null>(null);
    const completed = useRef<{ key: string; data: T } | null>(null);

    useEffect(() => () => {
        active.current?.controller.abort();
        active.current = null;
    }, []);

    // 保存・再計算・モデル変更で、実行中ジョブの応答と表示キャッシュを無効にする。
    const setData: Dispatch<SetStateAction<T | null>> = useCallback(value => {
        active.current?.controller.abort();
        active.current = null;
        completed.current = null;
        lastRequest.current = null;
        updateData(value);
        setLoading(false);
        setError(null);
        setLogs([]);
    }, []);

    const execute = useCallback((caseStudyId: number, modelIds: number[] = []): Promise<T | null> => {
        const params = { caseStudyId, modelIds: Array.from(new Set(modelIds)).sort((a, b) => a - b) };
        const key = JSON.stringify(params);
        if (active.current?.key === key) return active.current.promise;
        if (completed.current?.key === key) return Promise.resolve(completed.current.data);
        active.current?.controller.abort();
        const controller = new AbortController();
        lastRequest.current = params;
        completed.current = null;
        updateData(null);
        setError(null);
        setLogs([]);
        setLoading(true);
        const checkActive = () => {
            if (controller.signal.aborted) throw new DOMException('上流結果の監視を終了しました', 'AbortError');
        };
        const promise = (async (): Promise<T | null> => {
            try {
                const job = await postRequest<JobResult>(
                    `/case_studies/${caseStudyId}/${resultType}_result/upper/jobs`,
                    resultType === 'lcia' ? params.modelIds : undefined,
                );
                checkActive();
                while (true) {
                    const status = await getRequest<JobResult>(`/jobs/${job.job_id}`);
                    checkActive();
                    setLogs(status.logs);
                    const finished = [...status.logs].reverse().find(log => log.status === 'finished');
                    if (finished) {
                        if (finished.end_status !== 'success') {
                            const detail = status.logs.filter(log => log.status === 'running').slice(-1)[0]?.message;
                            throw new Error(detail || '上流フローの計算結果作成に失敗しました');
                        }
                        const result = await getRequest<T>(`/case_studies/${caseStudyId}/upper_result_jobs/${job.job_id}/result`);
                        checkActive();
                        completed.current = { key, data: result };
                        updateData(result);
                        return result;
                    }
                    // 処理全体に待機期限を設けず、前回の取得終了から順番に監視する。
                    await waitForNextPoll(controller.signal);
                    checkActive();
                }
            } catch (failure: any) {
                if (!controller.signal.aborted) {
                    const detail = failure.response?.data?.detail;
                    const hasResponse = Boolean(failure.response);
                    setError(typeof detail === 'string' ? detail :
                        failure instanceof Error && !hasResponse ? failure.message : '上流フローの計算結果を取得できませんでした。再試行してください');
                }
                // 呼び出し元がawaitしない画面でも、失敗は状態として保持し共通UIに表示する。
                return null;
            } finally {
                if (active.current?.controller === controller) {
                    active.current = null;
                    setLoading(false);
                }
            }
        })();
        active.current = { key, controller, promise };
        return promise;
    }, [resultType]);

    const retry = useCallback(() => {
        const params = lastRequest.current;
        if (params) void execute(params.caseStudyId, params.modelIds);
    }, [execute]);

    return { data, setData, loading, error, logs, execute, retry };
};
